import React, { FC } from "react";
import { useLocale } from "../i18n/locale";

export interface SignatureLaunchCardProps {
  /** Small caps label above the title — "Electronic signature". */
  eyebrow: string;
  /** Shown before signing; replaced by `signedTitle` afterwards. */
  title: string;
  signedTitle: string;
  /** Shown before signing, e.g. "Use your finger, stylus or mouse…". */
  caption: string;
  signedCaption: string;
  signed: boolean;
  /** PNG data URL of the drawn signature, previewed once present. */
  signature?: string;
  /** Label for the action button before signing. */
  actionLabel: string;
  onSign: () => void;
  /**
   * Blocks the hand-off while the step is not ready to be signed — the
   * inventory uses it until Europea's cleaning photos are on the record, since
   * there is nothing to review yet.
   */
  disabled?: boolean;
  /**
   * The signature is committed and can no longer be redrawn: the card becomes a
   * record of what was signed, with no action on it.
   *
   * Deliberately separate from `signed`. A tenant who has just drawn a poor
   * signature and is still on the step may redo it — that is a correction, not
   * a change of mind. What must not happen is going back afterwards to erase or
   * replace a signature the journey has already moved past, so this is driven
   * by the step being marked completed (which pressing Continue does), not by a
   * signature merely existing.
   */
  committed?: boolean;
  className?: string;
}

/**
 * The row that hands off to the signature screen, on both the documents step
 * and the inventory. Turns green once its document set has been signed and
 * offers to redo it.
 */
const SignatureLaunchCard: FC<SignatureLaunchCardProps> = ({
  eyebrow,
  title,
  signedTitle,
  caption,
  signedCaption,
  signed,
  signature,
  actionLabel,
  onSign,
  disabled = false,
  committed = false,
  className = "",
}) => {
  const { t } = useLocale();

  return (
    <div
      className={`flex flex-wrap items-center gap-3 border p-[12px_12px] mb-[12px] transition-colors min-[760px]:flex-nowrap ${
        signed ? "border-[#91a892] bg-[#f7faf7]" : "border-[#d7d0c5] bg-[#fbfaf7]"
      } ${className}`}
    >
      <span
        className={`flex h-[38px] w-[38px] flex-none items-center justify-center rounded-full text-[19px] ${
          signed ? "bg-[#e4eee6] text-[#4f7259]" : "bg-[#eee7dc] text-[#99713c]"
        }`}
        aria-hidden="true"
      >
        {signed ? "✓" : "✎"}
      </span>

      <div className="min-w-0 flex-1">
        <small className="block text-[12px] min-[760px]:text-[12px] font-normal uppercase tracking-[0.13em] text-[#9d8c78]">
          {eyebrow}
        </small>
        <strong className="block text-[13px] font-semibold text-navy">
          {signed ? signedTitle : title}
        </strong>
        <p className="m-0 text-[13px] min-[760px]:text-[13px] text-[#92979c]">
          {signed ? signedCaption : caption}
        </p>
      </div>

      {signature && (
        <img
          src={signature}
          alt=""
          className="h-10 w-[86px] flex-none border-b border-[#cbbda9] object-contain"
        />
      )}

      {/* Committed signatures keep the preview and the "signed by" caption but
          lose the action entirely — leaving a "Sign again" button there is what
          invites a tenant to replace a signature the journey has moved past. */}
      {!committed && (
      <button
        type="button"
        onClick={onSign}
        disabled={disabled}
        className={`w-full flex-none whitespace-nowrap px-[14px] py-2 text-[12px] min-[760px]:text-[12px] font-extrabold text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-[#c9c9c5] disabled:text-[#f1f1ee] disabled:hover:bg-[#c9c9c5] min-[760px]:w-auto ${
          signed
            ? "bg-[#5d7764] hover:bg-[#4f6856] focus-visible:ring-[#5d7764]"
            : "bg-gold hover:bg-[#a9854f] focus-visible:ring-gold"
        }`}
      >
        {signed ? t.desktop.contractPanel.signAgain : actionLabel}
      </button>
      )}
    </div>
  );
};

export default SignatureLaunchCard;
